import React, { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Paperclip, Upload, Link as LinkIcon, Trash2, FileText, ExternalLink, Plus
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api";

export default function MaterialsManager({ courseId, moduleIndex, materials, onChanged, readOnly = false }) {
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [tab, setTab] = useState("file");
  const [busy, setBusy] = useState(false);

  const items = materials || [];

  const uploadFile = async (file) => {
    if (!file) return;
    const t = title || file.name;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", t);
      await api.post(`/courses/${courseId}/modules/${moduleIndex}/materials/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Material adicionado");
      setTitle("");
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const addLink = async () => {
    if (!title || !url) return toast.error("Informe título e URL");
    setBusy(true);
    try {
      await api.post(`/courses/${courseId}/modules/${moduleIndex}/materials/link`, { title, url });
      toast.success("Link adicionado");
      setTitle(""); setUrl("");
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const removeMaterial = async (id) => {
    if (!window.confirm("Remover este material?")) return;
    try {
      await api.delete(`/courses/${courseId}/modules/${moduleIndex}/materials/${id}`);
      toast.success("Removido");
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const openMaterial = (m) => {
    if (m.type === "link") return m.url;
    return `${API}/files/${m.file_path}`;
  };

  return (
    <div data-testid={`materials-manager-${moduleIndex}`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="flex items-center gap-2"><Paperclip className="w-4 h-4" />Materiais complementares ({items.length})</Label>
      </div>

      {/* List */}
      <div className="space-y-2 mb-3">
        {items.length === 0 && (
          <div className="text-xs text-slate-500 italic">Nenhum material adicionado.</div>
        )}
        {items.map((m) => (
          <div key={m.id} className="flex items-center gap-3 bg-slate-50 border border-[var(--unigr-border)] rounded-md px-3 py-2 text-sm" data-testid={`material-${m.id}`}>
            {m.type === "file" ? <FileText className="w-4 h-4 text-[var(--unigr-navy)] shrink-0" /> : <LinkIcon className="w-4 h-4 text-[var(--unigr-red)] shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{m.title}</div>
              <div className="text-xs text-slate-500 truncate">
                {m.type === "file"
                  ? `${m.filename} · ${(m.size / 1024).toFixed(1)} KB`
                  : m.url}
              </div>
            </div>
            <a href={openMaterial(m)} target="_blank" rel="noopener noreferrer" data-testid={`material-open-${m.id}`}>
              <Button size="sm" variant="outline"><ExternalLink className="w-3 h-3 mr-1" />Abrir</Button>
            </a>
            {!readOnly && (
              <Button size="sm" variant="ghost" onClick={() => removeMaterial(m.id)} data-testid={`material-del-${m.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <Card className="border-dashed">
          <CardContent className="p-3">
            <div className="flex gap-2 mb-2 text-xs">
              <button type="button" onClick={() => setTab("file")} className={"px-3 py-1 rounded " + (tab === "file" ? "bg-[var(--unigr-navy)] text-white" : "bg-slate-100 text-slate-600")} data-testid={`material-tab-file-${moduleIndex}`}>Arquivo</button>
              <button type="button" onClick={() => setTab("link")} className={"px-3 py-1 rounded " + (tab === "link" ? "bg-[var(--unigr-navy)] text-white" : "bg-slate-100 text-slate-600")} data-testid={`material-tab-link-${moduleIndex}`}>Link externo</button>
            </div>
            {tab === "file" ? (
              <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label className="text-xs">Título (opcional)</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: POP-001 Tratativa de eventos" data-testid={`material-title-${moduleIndex}`} />
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.webp,.zip"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
                />
                <Button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-navy" data-testid={`material-upload-${moduleIndex}`}>
                  <Upload className="w-4 h-4 mr-2" />Enviar arquivo
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <Label className="text-xs">Título</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Vídeo no YouTube" data-testid={`link-title-${moduleIndex}`} />
                </div>
                <div>
                  <Label className="text-xs">URL</Label>
                  <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid={`link-url-${moduleIndex}`} />
                </div>
                <Button onClick={addLink} disabled={busy} className="btn-navy" data-testid={`link-add-${moduleIndex}`}>
                  <Plus className="w-4 h-4 mr-2" />Adicionar
                </Button>
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-2">
              Formatos aceitos: PDF, Office, imagens, ZIP (até 50MB) ou link externo.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
